{
  "account": {
    "title": "Account",
    "subtitle": "Your name is what Nolë calls you — it is handed to the assistant with every conversation. The rest comes from the provider you signed in with.",
    "notSignedIn": "You are not signed in.",
    "userId": "User ID",
    "name": {
      "placeholder": "How Nolë should call you",
      "providerHint": "Leave empty to keep “{{providerName}}”, the name your sign-in provider gave us.",
      "updated": "Name updated",
      "cleared": "Name cleared",
      "updateFailed": "Could not update your name"
    },
    "theme": {
      "label": "Theme",
      "light": "Light",
      "dark": "Dark",
      "system": "System"
    },
    "language": {
      "updateFailed": "Could not save your language"
    },
    "signOut": {
      "action": "Sign out",
      "confirmTitle": "Sign out?",
      "confirmText": "You will need to log in again to get back to your canvases."
    },
    "danger": {
      "title": "Danger zone",
      "description": "Deleting your account erases your canvases, your conversations and everything Nolë remembers. It cannot be undone."
    },
    "delete": {
      "action": "Delete my account",
      "title": "Delete your account",
      "description": "This cannot be undone. Deleting your account permanently erases:",
      "items": {
        "canvases": "your canvases, with every node, file and connection they hold",
        "conversations": "your conversations with Nolë, and everything she remembers about you",
        "skills": "your skills, custom nodes and API tokens",
        "sharedAccess": "your access to the canvases others have shared with you"
      },
      "exportHint": "Want to keep a copy? <exportLink>Export your data</exportLink> first — afterwards, nothing can be recovered.",
      "confirmLabel": "Type <email>{{email}}</email> to confirm",
      "done": "Your account has been deleted",
      "failed": "Could not delete your account"
    }
  },
  "groups": {
    "account": "Account",
    "customization": "Customization",
    "access": "Access"
  },
  "sections": {
    "account": "Account",
    "aiUsage": "AI usage",
    "export": "Export my data",
    "templates": "Custom nodes",
    "skills": "Skills",
    "canvas": "Canvas",
    "memories": "Agent Memory",
    "apiTokens": "MCP & API tokens",
    "sandbox": "Nolë's computer"
  },
  "shell": {
    "close": "Close settings",
    "openMenu": "Open settings menu",
    "pageError": "This page could not be displayed."
  },
  "export": {
    "title": "Export my data",
    "subtitle": "Download your canvases as a ZIP archive: one readable Markdown file per node, with the raw JSON next to it so nothing is lost. Images, PDFs and audio files are not bundled in — the archive references them by URL.",
    "progress": {
      "listing": "Listing canvases ({{total}})…",
      "zipping": "Building the archive…",
      "fetching": "Canvas {{current}} / {{total}}",
      "fetchingNamed": "Canvas {{current}} / {{total}} — {{name}}"
    },
    "done_one": "Export complete ({{count}} canvas)",
    "done_other": "Export complete ({{count}} canvases)",
    "failed": "Export failed",
    "loading": "Loading your canvases…",
    "empty": "You have no canvas to export yet",
    "scope": {
      "all": "Everything",
      "allDescription_one": "The {{count}} canvas you created, with all its nodes.",
      "allDescription_other": "The {{count}} canvases you created, with all their nodes.",
      "allDescriptionMore_one": "The {{count}} canvas you created (and more), with all their nodes.",
      "allDescriptionMore_other": "The {{count}} canvases you created (and more), with all their nodes.",
      "single": "A single canvas"
    },
    "loadMore": "Load more canvases",
    "action": "Export"
  },
  "memories": {
    "title": "Agent Memory",
    "subtitle": "What Nolë remembers about you, across all your canvases. One line is one memory — empty lines are ignored.",
    "user": {
      "title": "User memory",
      "description": "What Nolë remembers about you across all canvases."
    },
    "editor": {
      "overLimit": "Memory at {{length}}/{{max}} chars: exceeds the limit by {{excess}} chars. Remove or shorten entries before saving.",
      "saved": "Memories saved.",
      "saveFailed": "Failed to save the memories.",
      "usage": "{{percentage}}% — {{length}}/{{max}} chars",
      "placeholder": "One memory per line.",
      "hint": "One line = one memory. Empty lines are ignored. Nolë can also update these memories."
    }
  },
  "canvas": {
    "title": "Canvas",
    "subtitle": "Icon, color, cover image and background of this canvas, shared with everyone who can see it. Only its owner can edit them.",
    "noCanvas": "Open the settings from a canvas to change its appearance.",
    "peek": "Hold to preview",
    "background": {
      "color": "Background color",
      "usePreset": "Use {{color}} as background",
      "customColor": "Custom background color",
      "pattern": "Pattern",
      "variants": {
        "lines": "Lines",
        "dots": "Dots",
        "cross": "Cross",
        "none": "None"
      },
      "patternColor": "Pattern color",
      "customPatternColor": "Custom pattern color",
      "spacing": "Spacing ({{gap}}px)",
      "lineWidth": "Line width ({{size}})",
      "dotSize": "Dot size ({{size}})",
      "preview": "Preview",
      "previewLabel": "Background preview",
      "sharedHint": "Shared with everyone who can see this canvas."
    },
    "panel": {
      "loading": "Loading canvas…",
      "ownerOnly": "Only the owner of this canvas can change its appearance.",
      "saved": "Canvas updated — visible to everyone.",
      "saveFailed": "Could not update the canvas.",
      "cover": "Cover image",
      "coverSet": "Set",
      "coverHint": "Shown on the canvas card on the home page.",
      "background": "Background",
      "save": "Save changes",
      "resetBackground": "Reset background"
    },
    "identity": {
      "label": "Icon & color",
      "chooseIcon": "Choose an icon",
      "pasteEmoji": "Paste any emoji",
      "customEmoji": "Custom emoji",
      "use": "Use",
      "removeIcon": "Remove icon (use the initial)",
      "color": "Canvas color",
      "noColor": "No color"
    },
    "cover": {
      "invalidType": "Choose a PNG, JPEG, WebP, GIF or AVIF image.",
      "tooLarge": "This image is too large (5 MB max).",
      "previewAlt": "Cover preview",
      "replace": "Replace image",
      "upload": "Upload image"
    }
  },
  "sandbox": {
    "title": "Nolë's computer",
    "subtitle": "The computer in the cloud that Nolë uses for this canvas, and the websites it can visit.",
    "warning": {
      "title": "Why not allow the whole internet?",
      "risk": "Nolë reads what's on your canvas: documents, files, web pages you add. If one of them hides instructions, written by someone else, Nolë could be tricked into sending your data to their website. With the whole internet open, that data could go anywhere. With a short list, it can only go to websites you chose.",
      "advice": "So allow only websites you trust, one by one, and remove those you no longer need."
    },
    "noCanvas": "Open the settings from a canvas to manage its computer.",
    "sites": {
      "unavailable": "Nolë's computer isn't available here.",
      "addFailed": "Could not add this website",
      "removeFailed": "Could not remove this website",
      "title": "Websites Nolë's computer can visit",
      "description": "To keep this canvas's data safe, Nolë's computer is cut off from the internet. It can always download the software it needs, and it can visit the websites below, whoever uses Nolë on this canvas. Each of them could receive data from this canvas. Owners and editors can change the list.",
      "empty": "No website yet. Nolë asks you before visiting one.",
      "allowedBy": "Allowed by {{name}} on {{date}}",
      "allowed": "Allowed on {{date}}",
      "remove": "Remove {{host}}",
      "inputLabel": "Website to allow",
      "allow": "Allow",
      "full": "This canvas allows as many websites as it can: remove one to add another."
    }
  },
  "aiUsage": {
    "title": "AI usage",
    "subtitle": "What your conversations, sub-agents, thread titles and Nolë's computer cost, day by day. Amounts are the ones OpenRouter bills, and the computer's running time at its provider's price.",
    "empty": "No AI usage recorded over this period.",
    "periodLabel": "Usage period",
    "columns": {
      "cost": "Cost",
      "calls": "Calls",
      "tokens": "Tokens",
      "day": "Day (UTC)"
    },
    "missingCostCalls_one": "{{count}} call with no cost reported",
    "missingCostCalls_other": "{{count}} calls with no cost reported",
    "byModel": {
      "title": "By model",
      "model": "Model",
      "input": "Input",
      "output": "Output",
      "note": "Input includes cached tokens, output includes reasoning tokens."
    },
    "chart": {
      "tooltipStats_one": "{{tokens}} tk · {{calls}} call",
      "tooltipStats_other": "{{tokens}} tk · {{calls}} calls",
      "missingCost_one": "{{formatted}} with no cost reported",
      "missingCost_other": "{{formatted}} with no cost reported",
      "utcNote": "Days are counted in UTC, so late-evening activity in your timezone may land on the previous bar.",
      "monthlyNote": "Bars are monthly at this range; the table stays daily.",
      "stripedNote": "A striped bar understates its real spend — OpenRouter reported no cost for a significant share of its calls.",
      "empty": "No usage recorded over this period.",
      "costPerMonth": "Cost per month",
      "costPerDay": "Cost per day",
      "table": "Table",
      "chart": "Chart"
    },
    "summary": {
      "totalCost": "Total cost",
      "range": "{{range}} (UTC)",
      "tokensHint": "Input and output combined",
      "calls": "LLM calls",
      "callsHint": "One per model step, not per message",
      "missingCost_one": "<strong>{{missing}} of {{total}} calls reported no cost.</strong> Their tokens are counted, but their spend is missing from the total above — so the real amount is higher than what is shown.",
      "missingCost_other": "<strong>{{missing}} of {{total}} calls reported no cost.</strong> Their tokens are counted, but their spend is missing from the total above — so the real amount is higher than what is shown.",
      "truncated": "This period has more usage rows than a single read returns. The figures below are partial — narrow the period for an exact total."
    }
  },
  "apiTokens": {
    "title": "MCP & API tokens",
    "subtitle": "Create tokens to let third-party tools and agents (e.g. MCP servers) access the nolënor API on your behalf.",
    "hideRevoked": "Hide revoked ({{count}})",
    "showRevoked": "Show revoked ({{count}})",
    "onlyRevoked": "No active API token — only revoked ones.",
    "empty": "No API tokens yet",
    "permission": {
      "label": "Permission",
      "read": "Read",
      "write": "Write"
    },
    "list": {
      "token": "Token",
      "created": "Created",
      "lastUsed": "Last used",
      "never": "Never",
      "revokedOn": "Revoked {{date}}",
      "revokeConfirmTitle": "Revoke token “{{name}}”?",
      "revokeConfirmText": "Any application using it will immediately lose access.",
      "revoke": "Revoke",
      "revokeTitle": "Revoke token",
      "revokeFailed": "Failed to revoke token"
    },
    "create": {
      "trigger": "New token",
      "title": "New API token",
      "createdTitle": "Token created",
      "copyNow": "Copy this token now. For security reasons, it will not be shown again.",
      "copyToken": "Copy token",
      "copyCommand": "Copy command",
      "copied": {
        "token": "Token copied to clipboard",
        "command": "Command copied to clipboard"
      },
      "copyFailed": {
        "token": "Failed to copy token",
        "command": "Failed to copy command"
      },
      "connectClaudeCode": "Connect Claude Code",
      "otherClients": "Run it as a single line. Other MCP clients need the endpoint URL <mono>{{url}}</mono> with an <mono>Authorization: Bearer</mono> header — or, for clients like Claude Desktop that only offer preset header names, use <mono>x-api-key</mono> with the token as value.",
      "namePlaceholder": "e.g. My MCP agent",
      "creating": "Creating…",
      "submit": "Create token",
      "failed": "Failed to create token"
    },
    "edit": {
      "title": "Edit token",
      "updated": "Token updated",
      "failed": "Failed to update token"
    }
  },
  "skills": {
    "title": "Skills",
    "subtitle": "Reusable prompt modules Nolë can load on demand. Define the name, description, and body for discovery and usage.",
    "new": "New skill",
    "back": "All skills",
    "emptySelection": "Select a skill on the left, or create a new one.",
    "empty": "No skills yet.",
    "editor": {
      "notFound": "Skill not found.",
      "nameRequired": "Skill name is required.",
      "created": "Skill created.",
      "saved": "Skill saved.",
      "createFailed": "Failed to create skill.",
      "saveFailed": "Failed to save skill.",
      "deleted": "Skill “{{name}}” deleted.",
      "deleteFailed": "Failed to delete skill.",
      "noDescription": "No description yet",
      "draft": "Draft (unsaved)",
      "deleteConfirmTitle": "Delete skill “{{name}}”?",
      "deleteConfirmText": "This cannot be undone.",
      "deleteConfirm": "Delete skill",
      "namePlaceholder": "e.g. my_skill",
      "description": "Description",
      "descriptionPlaceholder": "Short description used to match this skill",
      "content": "Content",
      "contentHint": "The body is the full prompt loaded by Nolë. Stored as Markdown."
    },
    "attachments": {
      "nameRequired": "Attachment name is required.",
      "added": "Attachment “{{name}}” added.",
      "addFailed": "Failed to add attachment.",
      "removed": "Attachment “{{name}}” removed.",
      "removeFailed": "Failed to remove attachment.",
      "title": "Attachments ({{count}})",
      "add": "Add attachment",
      "empty": "No attachments.",
      "removeConfirmTitle": "Remove attachment “{{name}}”?",
      "removeConfirmText": "It will no longer be available to this skill.",
      "namePlaceholder": "e.g. extractor.py",
      "type": "Type",
      "types": {
        "md": "Markdown (.md)",
        "txt": "Plain text (.txt)",
        "script_py": "Python script (.py)",
        "script_ts": "TypeScript script (.ts)"
      },
      "contentPlaceholder": "Paste the attachment content here…",
      "adding": "Adding…"
    }
  },
  "templates": {
    "title": "Custom nodes",
    "subtitle": "Design node types for this canvas from a library of fields, with a layout for the canvas and another for the window. Every editor of the canvas can use and change them, and Nolë can read and write them like any other node.",
    "new": "New custom node",
    "noCanvas": "Custom nodes belong to a canvas: open these settings from a canvas to manage its custom nodes.",
    "draft": {
      "firstOption": "Option 1",
      "titleField": "Title",
      "name": "New template",
      "unnamedField": "A field has no name.",
      "duplicateName": "Two fields are named “{{name}}”."
    },
    "list": {
      "fields_one": "{{count}} field",
      "fields_other": "{{count}} fields",
      "databaseFields_one": "Database · {{count}} field",
      "databaseFields_other": "Database · {{count}} fields",
      "empty": "No templates yet."
    },
    "defaultValueHint": "Pre-filled in new nodes. Leave empty for none.",
    "modal": {
      "title": "Edit custom node template",
      "description": "Design the fields of a custom node template, and how its node and its window show them.",
      "discardTitle": "Discard unsaved changes?",
      "discardDescription": "This template has unsaved changes. Closing now will lose them.",
      "keepEditing": "Keep editing",
      "discard": "Discard changes",
      "header": "Template",
      "noCanvas": "Open a canvas to create a custom node: templates belong to a canvas.",
      "viewerCreate": "Only the editors of this canvas can create custom nodes.",
      "notFound": "This template doesn’t exist, or its canvas isn’t shared with you.",
      "viewerEdit": "Only the editors of this canvas can change its templates."
    },
    "fields": {
      "field": "Field",
      "notInLayout": "Not in this layout",
      "new": "New field",
      "empty": "No fields yet.",
      "addToLayout": "Add to this layout"
    },
    "preview": {
      "resize": "Resize",
      "dragToResize": "Drag to resize",
      "autoLayout": "Automatic layout: the title, then every field in order.",
      "customize": "Customize",
      "hiddenFields_one": "{{count}} field not in the layout",
      "hiddenFields_other": "{{count}} fields not in the layout"
    },
    "inspector": {
      "style": "Style",
      "levels": {
        "h2": "Heading 2",
        "h3": "Heading 3",
        "p": "Text"
      },
      "width": "Width",
      "widths": {
        "auto": "Auto",
        "fill": "Fill",
        "fixed": "Fixed"
      },
      "widthPx": "Width (px)",
      "heightPx": "Height (px)",
      "titleHint": "The title of the node, as a heading.",
      "showName": "Show the field name",
      "singleLine": "Single line",
      "singleLineHint": "Cut long text with an ellipsis",
      "text": "Text",
      "textPlaceholder": "Section title, hint, caption…",
      "maxWidth": "Max width (px)",
      "noMax": "None",
      "type": "Type",
      "title": "Title",
      "titleExplanation": "Every template has its title: it names its nodes in search, links and mentions.",
      "typeSettings": "{{type}} settings",
      "defaultValue": "Default value",
      "descriptionForNole": "Description for Nolë",
      "descriptionPlaceholder": "What this field holds, to help Nolë read and fill it",
      "usedBy": "Used by {{usage}}. Deleting the field hides their values without erasing them.",
      "deleteField": "Delete field",
      "sizeOnCanvas": "Size on the canvas",
      "resizable": "Resizable on the canvas",
      "sizeHint": "The size of new nodes. Drag the corner of the preview to change it.",
      "windowLayout": "Window layout",
      "customLayout": "Custom layout.",
      "customLayoutHidden_one": "Custom layout. {{count}} field is not in it: the window shows it folded below.",
      "customLayoutHidden_other": "Custom layout. {{count}} fields are not in it: the window shows them folded below.",
      "useAuto": "Use the automatic layout",
      "window": "Window",
      "autoLayoutHint": "Automatic layout: every field, one under the other, in the order of the fields list. New fields join it.",
      "customizeLayout": "Customize the layout"
    },
    "editor": {
      "unknownField": "Unknown field",
      "emptyText": "Empty text",
      "divider": "Divider",
      "rootNode": "Node",
      "addedToFieldsOnly": "{{name}} was added to the fields only. {{reason}}",
      "untitled": "Untitled template",
      "saved": "Template saved",
      "created": "Template created",
      "saveFailed": "Could not save the template",
      "deleted": "Template deleted",
      "deleteFailed": "Could not delete the template",
      "inAutoWindow": "Shown in the automatic window layout.",
      "notInNodeLayout": "Not in the node layout.",
      "notInWindowLayout": "Not in the window layout.",
      "addToNodeLayout": "Add to the node layout",
      "addToWindowLayout": "Add to the window layout",
      "removeFromLayout": "Remove from layout",
      "name": "Template name",
      "descriptionPlaceholder": "What it is for and when to use it, for you and Nolë",
      "usedBy": "Used by {{usage}}",
      "deleteConfirmTitle": "Delete this template?",
      "deleteConfirmText": "It will be deleted for every editor of this canvas. This cannot be undone.",
      "deleteBlocked": "Used by {{usage}}: delete them first, trash included",
      "delete": "Delete template",
      "layers": "Layers",
      "autoLayers": "Automatic: the title, then every field in order.",
      "fields": "Fields",
      "layoutLabel": "Layout",
      "nodeSurface": "Node on the canvas",
      "sampleValues": "Sample values",
      "samples": {
        "filled": {
          "label": "Filled",
          "title": "Typical values"
        },
        "empty": {
          "label": "Empty",
          "title": "A new node"
        },
        "overflow": {
          "label": "Long",
          "title": "Long values: shows the layouts that don't hold"
        }
      },
      "zoom": "Zoom",
      "moreErrors_one": "(+{{count}} more)",
      "moreErrors_other": "(+{{count}} more)",
      "changedElsewhere": "Changed elsewhere since you opened it: saving replaces those changes.",
      "status": {
        "dirty": "Unsaved changes. Nodes of this template update when you save.",
        "pristine": "Nothing changes until you save.",
        "new": "The template is created when you save."
      },
      "icon": "Icon"
    }
  }
}
